import { FALLBACK_CLASS_NAME } from "./company-classes";
import { countryOf } from "./formatters/location";
import { type Candidate } from "./types/candidate";

/** What the Anonym-Ansicht left out of a masked candidate, so screens can
 * show a hidden box instead of an empty spot. Only a missing anonymized view
 * withholds texts (fails closed); names in them are replaced, never hidden (#172). */
export interface AnonymHidden {
  strengths: boolean;
  weaknesses: boolean;
  /** Score-reasoning axes the anonymized view doesn't carry. */
  reasoningAxes: string[];
  /** Positions of career entries whose description was withheld. */
  careerDescriptions: number[];
}

export interface MaskedCandidate {
  candidate: Candidate;
  /** Null while the Anonym-Ansicht is off. */
  hidden: AnonymHidden | null;
}

/** Shown when a candidate has no code (should not happen for admins). */
const NO_CODE_NAME = "Kandidat";

/** The candidate as the Anonym-Ansicht shows it (#140): the code instead of
 * the name, no photo or contact data, employers as company classes, the
 * backend's cleaned texts, and no people-written texts. Links to clear data
 * (profiles, CV) stay for the confirm-then-open buttons, which never render
 * them. Without the backend's anonymized view everything identifying is
 * hidden: it fails closed. */
export function maskCandidate(c: Candidate, anonym: boolean): MaskedCandidate {
  if (!anonym) return { candidate: c, hidden: null };
  const a = c.anonymized ?? null;

  const careerTimeline = c.careerTimeline.map((entry, i) => {
    const view = a?.career[i];
    return {
      ...entry,
      company: view?.company ?? FALLBACK_CLASS_NAME,
      // A title can name the employer; without the cleaned one, none (fails closed).
      title: view?.title ?? "",
      description: view ? view.description : null,
    };
  });
  // An axis is hidden when it had text the anonymized view doesn't carry.
  const reasoningAxes = Object.keys(c.subReasoning).filter(
    (axis) => (c.subReasoning[axis]?.length ?? 0) > 0 && !a?.subReasoning[axis],
  );
  const subReasoning = Object.fromEntries(
    Object.keys(c.subReasoning).map((axis) => [axis, a?.subReasoning[axis] ?? []]),
  );

  return {
    candidate: {
      ...c,
      name: a?.code ?? c.code ?? NO_CODE_NAME,
      photoUrl: null,
      email: null,
      phone: null,
      // The country, never the town (#146); null when the location names none.
      location: countryOf(c.location),
      currentCompany: c.currentCompany ? (a?.currentCompany ?? FALLBACK_CLASS_NAME) : null,
      currentTitle: a ? a.currentTitle : null,
      careerTimeline,
      // Degree, subject and dates stay; a school or a "duales Studium bei X" can identify.
      educationEntries: c.educationEntries.map((e) => ({ ...e, school: "", description: null })),
      strengths: a?.strengths ?? [],
      weaknesses: a?.weaknesses ?? [],
      subReasoning,
      recruiterNotes: null,
      internalNotes: null,
      interviewNotes: null,
      interviewKeyFindings: [],
      recommendationText: null,
      rejectionReason: null,
    },
    hidden: {
      // `!` also catches a null list from a backend older than #172.
      strengths: !a?.strengths,
      weaknesses: !a?.weaknesses,
      reasoningAxes,
      careerDescriptions: careerTimeline
        .filter((e, i) => e.description === null && c.careerTimeline[i]?.description)
        .map((e) => e.position),
    },
  };
}

type Text = string | null | undefined;

const BRACKETED = /\[[^\]]+\]/;

/** Whether a shown text got a [bracketed] replacement its original didn't
 * have, so a card explains the brackets (#172). A dropped „@ BCG" or a
 * withheld text shows no brackets, so it doesn't count. */
export function showsReplacement(raw: readonly Text[], shown: readonly Text[]): boolean {
  return shown.some((text, i) => Boolean(text) && text !== raw[i] && BRACKETED.test(text ?? ""));
}
